import topicStyles from "../../TopicViewer.module.css";
import docStyles from "../../../Modus/DocumentWriter/DocumentWriter.module.css";
import {
  apiRequest,
  useMicRecorder,
  useModus,
  VojomoLoader,
  useDatadog
} from "@vojomo-core/shared";
import {
  Input,
  Checkbox,
  Button,
  Spin,
  Empty,
  ConfigProvider,
  Tooltip,
  message,
  Modal as AntModal
} from "antd";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSource } from "../../hooks/use-sources";
import { UploadOutlined } from "@ant-design/icons";
import { MdInfoOutline } from "react-icons/md";
import { FaRegWindowMaximize } from "react-icons/fa6";
import { FileUploadModals, useFileUpload } from "components/FileUpload";
import { CreateNoteModal, useCreateNote } from "components/CreateNote";
import { useTopic } from "pages/TopicViewer/hooks/use-topics";
import { useEffect, useMemo, useState } from "react";
import { TopicViewer } from "../../TopicViewer";

export const SourcesContent = () => {
  const { isBusy } = useMicRecorder();
  const { trackEvent, trackError } = useDatadog();

  const { isWaitingForResponse, modusChatContext, sendSystemMessage } =
    useModus();

  const { topic, selectedSources, setSelectedSources } = useTopic();

  const { isLoadingSources, sources, fetchSources } = useSource(topic["_id"]);

  const [searchTerm, setSearchTerm] = useState("");

  const [lastSelectedSources, setLastSelectedSources] = useState<string[]>([]);

  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // File Preview Modal State
  const [isFilePreviewModalVisible, setIsFilePreviewModalVisible] =
    useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<string | null>(null);
  const [isLoadingFileUrl, setIsLoadingFileUrl] = useState(false);

  // Topic Modal State
  const [topicModalOpened, { open: openTopicModal, close: closeTopicModal }] =
    useDisclosure(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [selectedTopicName, setSelectedTopicName] = useState<string | null>(
    null
  );

  // Filter sources based on search term and selected filter
  const filteredSources = useMemo(() => {
    let filtered = sources;

    // Filter by selected sources if enabled
    if (showOnlySelected) {
      filtered = filtered.filter((source) =>
        selectedSources.includes(source._id)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((source) => {
        const fileName = (source.file_name || "").toLowerCase();
        const fileType = (source.file_type || "").toLowerCase();
        return fileName.includes(lowerSearch) || fileType.includes(lowerSearch);
      });
    }

    return filtered;
  }, [sources, searchTerm, showOnlySelected, selectedSources]);

  // File upload hook
  const uploadHook = useFileUpload({
    onUploadSuccess: async (data) => {
      // Refresh sources and auto-select the new one
      await fetchSources();
      if (data.inserted_id) {
        setSelectedSources((prev) => {
          return [...prev, data.inserted_id];
        });
      }
    }
  });

  // Create note hook
  const createNoteHook = useCreateNote({
    onNoteCreated: async (data) => {
      // Refresh sources and auto-select the new note
      await fetchSources();
      if (data.inserted_id) {
        setSelectedSources((prev) => {
          return [...prev, data.inserted_id];
        });
      }
    }
  });

  // Handle file name change with debounced validation
  const handleFileNameChange = async (value: string) => {
    uploadHook.setNewFileName(value);

    if (uploadHook.isNewFileNameTaken) {
      uploadHook.setIsNewFileNameTaken(false);
    }

    if (value && value.trim() !== "" && uploadHook.currentFile) {
      const valueWithExt = uploadHook.ensureFileExtension(
        value,
        uploadHook.currentFile.name
      );
      if (valueWithExt !== uploadHook.currentFile.name) {
        const exists = await uploadHook.checkFileNameExists(valueWithExt);
        uploadHook.setIsNewFileNameTaken(exists);
      }
    }
  };

  useEffect(() => {
    if (!lastSelectedSources || !selectedSources) {
      return;
    }

    if (
      selectedSources.length === lastSelectedSources.length &&
      selectedSources.every((s) => lastSelectedSources.includes(s)) &&
      lastSelectedSources.every((s) => selectedSources.includes(s))
    ) {
      return;
    }

    const f = async () => {
      await apiRequest({
        subRoute: "topic",
        path: `/update_selected_sources`,
        method: "POST",
        body: {
          topicId: topic["_id"],
          selectedSources: selectedSources
        }
      });

      setLastSelectedSources(selectedSources);

      const systemUpdateMessage =
        selectedSources.length > 0
          ? "Sources updated.  " + Array.from(selectedSources)
          : "Sources cleared.";

      console.log("Checking if I should add sources");
      if (modusChatContext?.messages.some((msg) => msg.role === "system")) {
        console.log("Sending system message: " + systemUpdateMessage);
        sendSystemMessage(systemUpdateMessage);
      }
    };

    let debounceTimer: NodeJS.Timeout;
    const debouncedSave = () => {
      clearInterval(debounceTimer);
      debounceTimer = setTimeout(async () => {
        await f();
      }, 1500);
    };

    debouncedSave();
    return () => {
      if (debounceTimer) {
        clearInterval(debounceTimer);
      }
    };
  }, [lastSelectedSources, selectedSources]);

  useEffect(() => {
    setLastSelectedSources(selectedSources);
  }, []);

  const toggleSource = (sourceId: string) => {
    setSelectedSources((prev) => {
      if (prev.includes(sourceId)) {
        return prev.filter((s) => s !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  const selectAll = () => {
    setSelectedSources((prev) => {
      const newSet = new Set(prev);
      filteredSources.forEach((source) => newSet.add(source._id));
      return Array.from(newSet);
    });
  };

  const deselectAll = () => {
    setSelectedSources((prev) => {
      const newSet = new Set(prev);

      filteredSources.forEach((source) => newSet.delete(source._id));
      return Array.from(newSet);
    });
  };

  const handleFileClick = async (
    sourceId: string,
    fileName: string,
    fileType: string
  ) => {
    setIsLoadingFileUrl(true);
    setSelectedFileName(fileName);
    setSelectedFileType(fileType);
    setIsFilePreviewModalVisible(true);

    try {
      const response = await apiRequest<{
        url: string;
        file_type: string;
        file_name: string;
      }>({
        subRoute: "ai",
        path: `/get_source_url?source_id=${sourceId}`,
        method: "GET"
      });

      if (response?.url) {
        trackEvent("fetched_source", { response, fileName, fileType });
        setSelectedFileUrl(response.url);
      } else {
        trackError("error_no_url_in_response", null, response);
        message.error("Failed to load file preview - no URL returned");
        setIsFilePreviewModalVisible(false);
      }
    } catch (error: any) {
      trackError("error_fetching_file_url", error);

      const errorMsg =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to load file preview";
      message.error(errorMsg);
      setIsFilePreviewModalVisible(false);
    } finally {
      setIsLoadingFileUrl(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "rgb(64, 41, 117)",
            defaultBorderColor: "rgb(44, 21, 97)",
            defaultBg: "rgb(44, 21, 97)",
            defaultHoverBg: "rgb(64, 41, 117)"
          }
        }
      }}
    >
      <div className={topicStyles["main-content"]}>
        {/* Hidden file input */}
        <input
          ref={uploadHook.fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          style={{ display: "none" }}
          onChange={uploadHook.handleFileSelect}
        />

        {/* Upload Modals */}
        <FileUploadModals
          sameNameModalOpen={uploadHook.sameNameModalOpen}
          onCloseSameNameModal={uploadHook.resetUploadState}
          newFileName={uploadHook.newFileName}
          onFileNameChange={handleFileNameChange}
          isNewFileNameTaken={uploadHook.isNewFileNameTaken}
          currentFile={uploadHook.currentFile}
          onRenameAndUpload={uploadHook.handleRenameAndUpload}
          uploading={uploadHook.uploading}
          sameHashModalOpen={uploadHook.sameHashModalOpen}
          onCloseSameHashModal={uploadHook.resetUploadState}
          sameHashModalFiles={uploadHook.sameHashModalFiles}
          onContinueUpload={uploadHook.continueUploadAfterHashWarning}
        />

        {/* Create Note Modal */}
        <CreateNoteModal
          isOpen={createNoteHook.isModalOpen}
          noteName={createNoteHook.noteName}
          noteContent={createNoteHook.noteContent}
          isCreating={createNoteHook.isCreating}
          error={createNoteHook.error}
          isNameTaken={createNoteHook.isNameTaken}
          onClose={createNoteHook.closeModal}
          onNameChange={createNoteHook.handleNameChange}
          onContentChange={createNoteHook.setNoteContent}
          onCreate={createNoteHook.createNote}
        />

        <div
          className={docStyles["doc-writer-container"] + " px-5 py-4"}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden"
          }}
        >
          {/* Fixed Header Section */}
          <div style={{ flexShrink: 0 }}>
            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "16px",
                alignItems: "center"
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  icon={<UploadOutlined />}
                  onClick={uploadHook.triggerFileInput}
                  size="small"
                  style={{ color: "white" }}
                  disabled={isWaitingForResponse || isBusy}
                >
                  Upload
                </Button>
                <Button
                  onClick={createNoteHook.openModal}
                  size="small"
                  style={{ color: "white" }}
                  disabled={isWaitingForResponse || isBusy}
                >
                  + Create Note
                </Button>
              </div>
              <Tooltip title="Add sources (external sources or your other Vojomo documents) to use when generating content for this document.">
                <MdInfoOutline />
              </Tooltip>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "16px",
                alignItems: "center"
              }}
            >
              <Button
                onClick={() => setShowOnlySelected(!showOnlySelected)}
                size="small"
                style={{ color: "white" }}
                type="default"
              >
                {showOnlySelected ? "Show All" : "Show Selected"}
              </Button>
              <Button
                style={{ color: "white" }}
                onClick={selectAll}
                size="small"
                disabled={isWaitingForResponse || isBusy}
              >
                Select All
              </Button>
              <Button
                style={{ color: "white" }}
                onClick={deselectAll}
                size="small"
                disabled={isWaitingForResponse || isBusy}
              >
                Deselect All
              </Button>
            </div>
            {/* Search Bar */}
            <div style={{ marginBottom: "12px" }}>
              <Input.Search
                placeholder="Search sources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                style={{ width: "100%" }}
                className="custom-search-input"
              />
              <style>{`
                .custom-search-input .ant-input-search-button {
                  background-color: white !important;
                  border-color: #d9d9d9 !important;
                }
                .custom-search-input .ant-input-search-button:hover {
                  background-color: white !important;
                  border-color: #d9d9d9 !important;
                }
                .custom-search-input .ant-btn-icon {
                  color: #000000 !important;
                }
              `}</style>
            </div>
            {/* Selected count */}
            <div
              style={{
                marginBottom: "16px",
                color: "#666",
                fontSize: "14px"
              }}
            >
              {selectedSources.length} source
              {selectedSources.length !== 1 ? "s" : ""} selected
              {searchTerm && ` (${filteredSources.length} shown)`}
            </div>
          </div>

          {/* Scrollable Sources List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "8px",
              scrollbarGutter: "stable"
            }}
          >
            {isLoadingSources ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px"
                }}
              >
                <Spin size="large" />
              </div>
            ) : filteredSources.length === 0 ? (
              <Empty
                description={
                  searchTerm
                    ? "No sources match your search"
                    : "No sources available. Upload more documents here or in Manage Sources."
                }
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px"
                }}
              >
                {filteredSources.map((source) => (
                  <div
                    key={source._id}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      backgroundColor: selectedSources.includes(source._id)
                        ? "#f0f5ff"
                        : "#fff",
                      cursor:
                        isWaitingForResponse || isBusy
                          ? "not-allowed"
                          : "pointer",
                      transition: "all 0.2s",
                      opacity: isWaitingForResponse || isBusy ? 0.6 : 1
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                      }}
                    >
                      <Checkbox
                        checked={selectedSources.includes(source._id)}
                        onChange={() => toggleSource(source._id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isWaitingForResponse || isBusy}
                      />
                      <div
                        style={{ flex: 1, cursor: "pointer" }}
                        onClick={() => {
                          // Check if this is a Vojomo document (topic source)
                          if (source.file_type.startsWith("topic/")) {
                            // Open all Vojomo documents (including building blocks) in modal
                            // Handle both string and ObjectId format
                            const topicId =
                              typeof source.topic === "string"
                                ? source.topic
                                : source.topic?.$oid;

                            setSelectedTopicName(source.file_name);
                            setSelectedTopicId(topicId);
                            openTopicModal();
                          } else {
                            // Show modal for uploaded files
                            handleFileClick(
                              source._id,
                              source.file_name,
                              source.file_type
                            );
                          }
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#1890ff",
                            textDecoration: "underline"
                          }}
                        >
                          {source.file_name}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* File Preview Modal */}
        {isFilePreviewModalVisible && (
          <AntModal
            open={true}
            width="80%"
            style={{ top: 20, maxHeight: "90vh" }}
            bodyProps={{
              style: {
                padding: "20px",
                height: "calc(90vh - 110px)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              }
            }}
            onCancel={() => {
              setIsFilePreviewModalVisible(false);
              setSelectedFileUrl(null);
              setSelectedFileName(null);
              setSelectedFileType(null);
            }}
            footer={[
              <Button
                key="close"
                style={{ color: "white" }}
                onClick={() => {
                  setIsFilePreviewModalVisible(false);
                  setSelectedFileUrl(null);
                  setSelectedFileName(null);
                  setSelectedFileType(null);
                }}
              >
                Close
              </Button>
            ]}
          >
            <div style={{ marginBottom: "16px", flexShrink: 0 }}>
              <h3 style={{ margin: 0 }}>{selectedFileName}</h3>
            </div>
            {isLoadingFileUrl ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <VojomoLoader />
              </div>
            ) : selectedFileUrl ? (
              <div
                style={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {(() => {
                  const fileType = selectedFileType?.toLowerCase() || "";
                  const fileName = selectedFileName?.toLowerCase() || "";
                  const isPdf =
                    fileType === "application/pdf" || fileType === "pdf";
                  const isDocx =
                    fileType.includes("word") ||
                    fileType === "docx" ||
                    fileName.endsWith(".docx");
                  const isImage =
                    fileType.includes("image") ||
                    [
                      "jpg",
                      "jpeg",
                      "png",
                      "gif",
                      "bmp",
                      "webp",
                      "svg"
                    ].includes(fileType);
                  const isText =
                    fileType === "text/plain" ||
                    [
                      "txt",
                      "md",
                      "json",
                      "xml",
                      "csv",
                      "log",
                      "yaml",
                      "yml",
                      "html",
                      "css",
                      "js",
                      "ts",
                      "tsx",
                      "jsx",
                      "py",
                      "java",
                      "c",
                      "cpp",
                      "h",
                      "sh"
                    ].includes(fileType);

                  if (isDocx || isPdf) {
                    // Use Google Docs Viewer for DOCX, direct URL for PDF
                    const viewerUrl = isDocx
                      ? `https://docs.google.com/viewer?url=${encodeURIComponent(selectedFileUrl)}&embedded=true`
                      : selectedFileUrl;

                    return (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column"
                        }}
                      >
                        <iframe
                          src={viewerUrl}
                          style={{
                            width: "100%",
                            flex: 1,
                            border: "none",
                            minHeight: 0
                          }}
                          title={selectedFileName || "File Preview"}
                          onError={() => {
                            trackError("file_iframe_failed_to_load");
                          }}
                        />
                        <div
                          style={{
                            textAlign: "center",
                            padding: "12px 0",
                            borderTop: "1px solid #f0f0f0",
                            flexShrink: 0
                          }}
                        >
                          <a
                            href={selectedFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button type="link" size="small">
                              Download File
                            </Button>
                          </a>
                        </div>
                      </div>
                    );
                  } else if (isImage) {
                    return (
                      <img
                        src={selectedFileUrl}
                        alt={selectedFileName || "Preview"}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain"
                        }}
                      />
                    );
                  } else if (isText) {
                    return (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          overflow: "auto",
                          backgroundColor: "#f5f5f5",
                          padding: "16px",
                          fontFamily: "monospace",
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        <iframe
                          src={selectedFileUrl}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none"
                          }}
                          title={selectedFileName || "File Preview"}
                        />
                      </div>
                    );
                  } else {
                    return (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px"
                        }}
                      >
                        <p>Preview not available for this file type.</p>
                        <a
                          href={selectedFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button type="primary">Download File</Button>
                        </a>
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>Failed to load file preview</p>
              </div>
            )}
          </AntModal>
        )}

        {/* Topic Modal */}
        {selectedTopicId && (
          <Modal.Root
            opened={topicModalOpened}
            onClose={() => {
              closeTopicModal();
              setSelectedTopicId(null);
              setSelectedTopicName(null);
            }}
            centered
            style={{
              zIndex: 2,
              backgroundColor: "#f7f8fc"
            }}
            size="90%"
            styles={{
              body: {
                backgroundColor: "#f7f8fc"
              },
              header: {
                backgroundColor: "#f7f8fc"
              }
            }}
          >
            <Modal.Overlay />
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>{selectedTopicName}</Modal.Title>
                <Modal.CloseButton />
                <Tooltip title="Open in New Tab">
                  <FaRegWindowMaximize
                    style={{
                      fontWeight: 600,
                      color: "var(--mantine-color-gray-7)",
                      fontSize: "16px",
                      cursor: "pointer",
                      marginLeft: "5px"
                    }}
                    onClick={() =>
                      window.open(
                        `/documents/docs/${selectedTopicId}`,
                        "_blank"
                      )
                    }
                  />
                </Tooltip>
              </Modal.Header>
              <Modal.Body>
                <TopicViewer
                  topicIdProps={selectedTopicId}
                  inModalView={true}
                  readOnly={true}
                />
              </Modal.Body>
            </Modal.Content>
          </Modal.Root>
        )}
      </div>
    </ConfigProvider>
  );
};
